import { ErrorCode } from '@gr/core/errors';
import { MediaKind } from '@gr/core/content/media/formats';
import {
  RECOMPRESS_LIMITS,
  RECOMPRESS_PRESETS,
  recompressSourceKind,
  RecompressImageTarget,
  RecompressPreset,
  RecompressVideoTarget,
  type MediaStorageDto,
  type RecompressParamsDto,
} from '@gr/core/content/media/recompress';

// MED-UC-9: форма пережатия — пресеты, свои параметры, выбранные форматы. Без React.

/** Числа — строками, как их вводит пользователь; пустая строка у размеров и частоты — «без ограничения». */
export interface RecompressFormValues {
  sources: Record<string, boolean>;
  preset: RecompressPreset;
  imageTarget: RecompressImageTarget | '';
  videoTarget: RecompressVideoTarget | '';
  quality: string;
  maxLongSide: string;
  crf: string;
  maxShortSide: string;
  maxFps: string;
  audioKbps: string;
  minSavingsPercent: string;
}

export type RecompressNumberField = 'quality' | 'maxLongSide' | 'crf' | 'maxShortSide' | 'maxFps' | 'audioKbps' | 'minSavingsPercent';

/** Варианты выпадающих списков формы; null — без ограничения. */
export const LONG_SIDE_OPTIONS: readonly (number | null)[] = [null, 6144, 4096, 3072, 2560, 2048, 1600];
export const SHORT_SIDE_OPTIONS: readonly (number | null)[] = [null, 2160, 1440, 1080, 720, 480];
export const FPS_OPTIONS: readonly (number | null)[] = [null, 60, 30, 25];
export const AUDIO_OPTIONS: readonly number[] = [96, 128, 160, 192, 256];

/** Обычно тяжёлые исходники с телефона и камеры — выбраны сразу, если они есть в блоге. */
const HEAVY_SOURCES = ['heic', 'heif', 'dng', 'tif', 'tiff', 'mov'];

const text = (value: number | null): string => (value === null ? '' : String(value));

/** Значения полей пресета. */
export function presetFields(preset: Exclude<RecompressPreset, RecompressPreset.Custom>): Pick<RecompressFormValues, RecompressNumberField> {
  const values = RECOMPRESS_PRESETS[preset];
  return {
    quality: text(values.image.quality),
    maxLongSide: text(values.image.maxLongSide),
    crf: text(values.video.crf),
    maxShortSide: text(values.video.maxShortSide),
    maxFps: text(values.video.maxFps),
    audioKbps: text(values.video.audioKbps),
    minSavingsPercent: text(values.minSavingsPercent),
  };
}

/** Начальные значения: «Сбалансированно», первые доступные форматы, тяжёлые исходники выбраны. */
export function recompressFormValues(storage: MediaStorageDto): RecompressFormValues {
  const { imageTargets, videoTargets } = storage.recompress;
  return {
    sources: Object.fromEntries(storage.formats.filter((format) => recompressSourceKind(format.ext) !== null).map((format) => [format.ext, HEAVY_SOURCES.includes(format.ext)])),
    preset: RecompressPreset.Balanced,
    imageTarget: imageTargets.includes(RecompressImageTarget.Jpeg) ? RecompressImageTarget.Jpeg : (imageTargets[0] ?? ''),
    videoTarget: videoTargets[0] ?? '',
    ...presetFields(RecompressPreset.Balanced),
  };
}

export function selectedSources(values: RecompressFormValues): string[] {
  return Object.entries(values.sources)
    .filter(([, selected]) => selected)
    .map(([ext]) => ext);
}

/** MED-UC-9.CC-1: сколько файлов и какого объёма будет пережато. */
export function selectionSummary(storage: MediaStorageDto, values: RecompressFormValues): { files: number; bytes: number; images: boolean; videos: boolean } {
  const chosen = new Set(selectedSources(values));
  const formats = storage.formats.filter((format) => chosen.has(format.ext));
  return {
    files: formats.reduce((sum, format) => sum + format.count, 0),
    bytes: formats.reduce((sum, format) => sum + format.bytes, 0),
    images: formats.some((format) => format.kind === MediaKind.Image),
    videos: formats.some((format) => format.kind === MediaKind.Video),
  };
}

/** Проверка числа по границам; пустое у размеров и частоты допустимо. */
export function numberFieldError(field: RecompressNumberField, value: string): ErrorCode | null {
  const trimmed = value.trim();
  const optional = field === 'maxLongSide' || field === 'maxShortSide' || field === 'maxFps';
  if (trimmed === '') return optional ? null : ErrorCode.FieldRequired;
  const number = Number(trimmed);
  if (!Number.isInteger(number)) return ErrorCode.FieldNotNumber;
  const range = RECOMPRESS_LIMITS[field];
  return number < range.min || number > range.max ? ErrorCode.FieldOutOfRange : null;
}

const nullable = (value: string): number | null => (value.trim() === '' ? null : Number(value));

/** Значения формы → параметры задачи; параметры вида без выбранных исходников не отправляются. */
export function recompressParams(storage: MediaStorageDto, values: RecompressFormValues): RecompressParamsDto {
  const summary = selectionSummary(storage, values);
  return {
    sources: selectedSources(values),
    image:
      summary.images && values.imageTarget
        ? { target: values.imageTarget, quality: Number(values.quality), maxLongSide: nullable(values.maxLongSide) }
        : null,
    video:
      summary.videos && values.videoTarget
        ? { target: values.videoTarget, crf: Number(values.crf), maxShortSide: nullable(values.maxShortSide), maxFps: nullable(values.maxFps), audioKbps: Number(values.audioKbps) }
        : null,
    minSavingsPercent: Number(values.minSavingsPercent),
    preset: values.preset,
  };
}

/** Доля сэкономленного места, 0…1; null — пережатого нет. */
export function savedShare(before: number, after: number): number | null {
  return before > 0 ? Math.max(0, (before - after) / before) : null;
}
